import { HttpClient } from "@angular/common/http";
import { Injectable, effect, inject, signal } from "@angular/core";
import {
	EMPTY,
	type Observable,
	Subject,
	interval,
	map,
	of,
	takeUntil,
	tap,
} from "rxjs";
import type {
	Availability,
	CreateNewUserInput,
	UpdateUserInput,
	User,
} from "src/app/schema";
import {
	ApiService,
	CognitoService,
	OpenReplayService,
	WorkspaceService,
} from "src/app/services";
import { environment } from "src/environments/environment";

@Injectable({
	providedIn: "root",
})
export class UserService {
	private _destroyed$: Subject<void> = new Subject<void>();

	private readonly _httpClient = inject(HttpClient);
	private readonly _api = inject(ApiService);
	private readonly _cognitoService = inject(CognitoService);
	private readonly _openReplayService = inject(OpenReplayService);

	private _usersByPropertyCache = new Map<string, User[]>();
	private readonly _workspaceService = inject(WorkspaceService);

	public user: User | undefined;

	constructor() {
		effect(() => {
			try {
				const workspace = this._workspaceService.workspace();
				if (workspace?.id) {
					this.loadUsersByProperty(workspace.id)
						.pipe(takeUntil(this._destroyed$))
						.subscribe();
				}
			} catch (error) {}
		});
	}

	public activeUser$: Observable<User> = this.activeUser().pipe(
		tap((user) => {
			if (!this.user) {
				this._openReplayService.identifyUser(user);
			}
			this.user = user;
		}),
	);

	// public usersByProperty(propertyId: string): Observable<User[]> {
	// 	return this._httpClient.get<User[]>(
	// 		`${this._api.coreApiEndpoint}/properties/${propertyId}/users`,
	// 	);
	// }

	public usersByWorkspace(): Observable<User[]> {
		const { id: workspaceId } = this._workspaceService.workspace();

		if (!workspaceId) {
			return of([]);
		}

		return this.usersByProperty(workspaceId);
	}

	public usersByProperty(propertyId: string): Observable<User[]> {
		// // If we have data already, return it immediately
		// if (this._usersByPropertyCache.has(propertyId)) {
		// 	return of(this._usersByPropertyCache.get(propertyId) || []);
		// }

		// // Otherwise, load the data
		return this.loadUsersByProperty(propertyId);
	}

	public loadUsersByProperty(propertyId: string): Observable<User[]> {
		return this._httpClient
			.get<User[]>(
				`${this._api.coreApiEndpoint}/properties/${propertyId}/users`,
			)
			.pipe(
				tap((users) => {
					// Update cache with loaded data
					this._usersByPropertyCache.set(propertyId, users);
				}),
			);
	}

	public getUser(userId: string): Observable<User> {
		return this._httpClient.get<User>(
			`${this._api.coreApiEndpoint}/user/${userId}`,
		);
	}

	private activeUser(): Observable<User> {
		const userId: string | undefined = this._cognitoService.user().id;
		if (!userId) {
			return EMPTY;
		}
		return this._httpClient.get<User>(
			`${this._api.coreApiEndpoint}/user/${this._cognitoService.user().id}`,
		);
	}

	public createNewUser(
		user: CreateNewUserInput,
		propertyId: string,
	): Observable<User> {
		return this._httpClient.post<User>(
			`${environment.coreEndpoint}/properties/${propertyId}/user`,
			user,
		);
	}

	public updateAvailability(availability: Availability[]): Observable<User> {
		return this._httpClient.put<User>(
			`${this._api.calendaringApiEndpoint}/availability`,
			availability,
		);
	}

	// public updateUser(user: UpdateUserInput): Observable<User> {
	// 	return this._httpClient.put<User>(
	// 		`${this._api.coreApiEndpoint}/user`,
	// 		user,
	// 	);
	// }

	// // TODO: Call this method as soon as the user is authenticated. The ngOnInit() was not getting called in this service.
	// public toggleLastUserContactSubscription(): void {
	// 	const source = interval(20000);
	// 	source.subscribe(() => {
	// 		const user = {
	// 			id: this._cognitoService.user().id,
	// 			lastContact: new Date().toISOString(),
	// 		};
	// 		this.updateUser(user);
	// 	});
	// }
}
